import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alliance, GovernyInfo, WarInfo } from "../../../../lib/types";
import { portraits } from "../../../../assets/images/races";
import black from "../../../../assets/images/wllpprs/black.jpg";
import { CONTENT_CONTEXT } from "../../../../lib/enums";
import WarStatus from "./WarStatus";
import { isGovernorateEmperor } from "../../../../lib/commonFunctions";
import { openDialog } from "../../../../store/actions";
import { DragescentState } from "../../../../store/reducers/stateType";
import GovernorateInfoLink from "../../../Common/GovernorateInfoLink";

interface IProps {
  alliance: Alliance;
  owner: boolean;
  war?: WarInfo;
  opponent?: Alliance;
}

const AllianceProfile = ({
  alliance,
  owner,
  war,
  opponent
}: IProps) => {
  const actionsButtonRefs = useRef<any>([]);
  const editNoteRef = useRef<HTMLAnchorElement>(null);

  const dispatch = useDispatch();
  const id = useSelector((state: DragescentState) => state.governorate.id);

  return (
    <section className="boxes-group">
      <div className="box first padding">
        <div className="row">
          <div className="col-xs-10 col-xs-offset-1 col-sm-offset-0 col-sm-6 text-center portraits">
            {alliance.members
              .map((governy: GovernyInfo) => <img
                width={96}
                height={96}
                src={portraits[governy.race.name]}
                alt={"Race portrait"}
              />)
            }
            {Array(4 - alliance.members.length).map(() => <img className="js-empty" width={96} height={96} src={black}
                                                               alt={"Race portrait filler"}/>)}
          </div>
          <div className="col-sm-8 col-md-6">
            <table className="table table-hover">
              <tbody>
              <tr>
                <td><strong>Land</strong></td>
                <td>
                  {alliance.landPosition === 1
                    ? <strong><em>{alliance.land}</em></strong>
                    : <>{alliance.land}</>
                  }
                </td>
                <td className="text-right">
                  {alliance.landPosition === 1
                    ? <strong><em>{alliance.landPosition}. position</em></strong>
                    : <>{alliance.landPosition}. position</>
                  }
                </td>
              </tr>
              <tr>
                <td><strong>Power</strong></td>
                <td>
                  {alliance.powerPosition === 1
                    ? <strong><em>{alliance.power}</em></strong>
                    : <>{alliance.power}</>
                  }
                </td>
                <td className="text-right">
                  {alliance.powerPosition === 1
                    ? <strong><em>{alliance.powerPosition}. position</em></strong>
                    : <>{alliance.powerPosition}. position</>
                  }
                </td>
              </tr>
              <tr>
                <td><strong>Members</strong></td>
                <td>
                  {alliance.members.length}
                </td>
              </tr>
              <tr>
                <td><strong>Rating</strong></td>
                <td>{alliance.rating}</td>
              </tr>
              <tr>
                <td><strong>LJP Points</strong></td>
                <td>{alliance.ljpPoints}</td>
              </tr>
              <tr>
                <td><strong>Emperor</strong></td>
                {alliance.emperor
                  ? <td>
                    <GovernorateInfoLink
                      govId={alliance.emperor.id}
                      name={alliance.emperor.name}
                      raceName={alliance.emperor.race.name}
                    />
                  </td>
                  : <td>
                    Empty coalition
                  </td>}
              </tr>
              </tbody>
            </table>

            {alliance.note || alliance.emperor.id === id
              ? <div className="motto-wrap">
                <blockquote className="motto">{alliance.note}</blockquote>
                {alliance.emperor.id === id
                  ? <a ref={editNoteRef} className={"link"} onClick={() => dispatch(openDialog({
                    name: "EditNote",
                    header: "Personal note",
                    elementRef: editNoteRef.current,
                    note: alliance.note,
                    type: CONTENT_CONTEXT.Alliance
                  }))}
                  >{`${alliance.note ? "Edit" : "Add own"} message`}</a> : <></>
                }
              </div> : <></>}
          </div>
        </div>
      </div>

      <h2 className="h2 h2-first">
            <span className="bordered">
            <span className="text-center">War status</span>
            </span>
      </h2>
      <span className="divider"/>
      <div className="box" id="war-status">
        <WarStatus
          owner={owner}
          alliance={alliance}
          opponent={opponent}
          war={war}
        />
      </div>
      <span className="divider"/>
      <h2 className="h2 h2-first">
            <span className="bordered">
            <span className="text-center">Overview</span>
            </span>
      </h2>
      <span className="divider"/>
      <div className="box imp-overview padding scrollable">
        <table className="table table-hover">
          <thead>
          <tr>
            <th className="text-left">Governorate</th>
            <th>Land</th>
            <th>Power</th>
            <th>LJP</th>
            <th>Rating</th>
            <th>Last action</th>
            {isGovernorateEmperor(id, alliance) ? <th>&nbsp;</th> : <></>}
          </tr>
          </thead>
          <tbody>
          {alliance.members
            .map((member: GovernyInfo) => <tr>
              <td className="no-padding-left">
                <span>
                  <img width={37} height={37} src={portraits[member.race.name]} alt={"Race portrait"}/>
                </span>
                <span style={{ display: "inline-table", verticalAlign: "super" }}>
                    <GovernorateInfoLink
                      govId={member.id}
                      name={member.name}
                      raceName={member.race.name}
                    /><br/>
                  {member.commander && <i className={`icon icon-vojevudce icon-xs ${member.race.name}`}/>}
                  {alliance.emperor.id === member.id && <i className={`icon icon-imperator icon-xs ${member.race.name}`}/>}
                </span>
              </td>
              <td>{member.land}</td>
              <td>{member.power}</td>
              <td>{member.ljp}</td>
              <td>{member.rating}</td>
              <td style={{ whiteSpace: "nowrap" }}>{member.lastAction}</td>
              {isGovernorateEmperor(id, alliance) && member.id !== id
                ? <td>
                  <a
                    href="#"
                    ref={(el) => {
                      if (el) {
                        actionsButtonRefs.current[member.id] = el
                      }
                    }}
                    onClick={() => dispatch(openDialog({
                      name: "MemberActions",
                      header: `Edit ${member.name}`,
                      id: member.id,
                      memberName: member.name,
                      commander: member.commander,
                      elementRef: actionsButtonRefs.current[member.id]
                    }))}
                    className={`btn btn-sm btn-blank btn-no-width`}
                  >
                    Edit
                  </a>
                </td> : <></>}
            </tr>)}
          {owner && alliance.membershipApplications.length
            ? <tr className="carousel-title">
              <td colSpan={2}>
                <strong>Alliance applications</strong>
              </td>
              <td>&nbsp;</td>
            </tr> : <></>}
          {alliance.membershipApplications.map((applicant: GovernyInfo) => (<tr>
            <td className="no-padding-left">
                <span>
                  <img width={37} height={37} src={portraits[applicant.race.name]} alt={"Race portrait"}/>
                </span>
              <span style={{ display: "inline-table", verticalAlign: "super" }}>
                <GovernorateInfoLink
                  govId={applicant.id}
                  name={applicant.name}
                  raceName={applicant.race.name}
                /><br/>
              </span>
            </td>
            <td>{applicant.land}</td>
            <td>{applicant.power}</td>
            <td>{applicant.ljp}</td>
            <td>{applicant.rating}</td>
            <td style={{ whiteSpace: "nowrap" }}>{applicant.lastAction}</td>
            {isGovernorateEmperor(id, alliance)
              ? <td>
                <a
                  href="#"
                  ref={(el) => {
                    if (el) {
                      actionsButtonRefs.current[applicant.id] = el
                    }
                  }}
                  onClick={() => dispatch(openDialog({
                    name: "ApplicationActions",
                    header: `Applicant ${applicant.name}`,
                    id: applicant.id,
                    allowed: !war && alliance.members.length < 4,
                    elementRef: actionsButtonRefs.current[applicant.id]
                  }))}
                  className={"btn btn-sm btn-blank btn-no-width"}
                >
                  Edit
                </a>
              </td> : <></>}
          </tr>))
          }
          </tbody>
        </table>
      </div>
      <span className="divider" id="coal-messages"/>
    </section>
  )
}

export default AllianceProfile;
